import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Briefcase, MapPin, Clock, DollarSign, ExternalLink, Phone, User } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Jobs = () => {
  const { language } = useLanguage();
  const [jobs, setJobs] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [applicationData, setApplicationData] = useState({ name: '', phone: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [jobsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/jobs`),
        axios.get(`${API}/settings`)
      ]);
      setJobs(jobsRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error('Failed to load jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyClick = (job) => {
    setSelectedJob(job);
    setShowApplicationForm(true);
    setApplicationData({ name: '', phone: '' });
  };

  const handleSubmitApplication = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/job-applicants`, {
        name: applicationData.name,
        phone: applicationData.phone,
        job_id: selectedJob.id,
        job_title: language === 'en' ? selectedJob.title_en : selectedJob.title_hi
      });
      
      toast.success(
        language === 'en'
          ? 'Application submitted successfully! We will contact you soon.'
          : 'आवेदन सफलतापूर्वक जमा हो गया! हम जल्द ही आपसे संपर्क करेंगे।'
      );
      
      // Open external form if available
      if (selectedJob?.google_form_url) {
        window.open(selectedJob.google_form_url, '_blank');
      }
      
      setShowApplicationForm(false);
      setSelectedJob(null);
      setApplicationData({ name: '', phone: '' });
    } catch (error) {
      toast.error(
        language === 'en'
          ? 'Failed to submit application. Please try again.'
          : 'आवेदन जमा करने में विफल। कृपया पुनः प्रयास करें।'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleExternalApply = () => {
    if (settings?.jobs_google_form_url) {
      window.open(settings.jobs_google_form_url, '_blank');
    }
  };

  const jobTypeLabels = {
    'full-time': { en: 'Full Time', hi: 'पूर्णकालिक' },
    'part-time': { en: 'Part Time', hi: 'अंशकालिक' },
    'contract': { en: 'Contract', hi: 'अनुबंध' }
  };

  return (
    <div className="min-h-screen" data-testid="jobs-page">
      <Navbar />
      
      <div className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-brand-teal-tint to-white">
        <div className="max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-brand-teal-tint mb-6">
              <Briefcase className="h-10 w-10 text-brand-teal" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-heading font-bold text-foreground mb-4">
              {language === 'en' ? 'Career Opportunities' : 'करियर के अवसर'}
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {language === 'en'
                ? 'Join our team and make a difference in healthcare. Explore current openings below.'
                : 'हमारी टीम में शामिल हों और स्वास्थ्य सेवा में बदलाव लाएं। नीचे वर्तमान पदों को देखें।'}
            </p>
          </div>

          {/* Jobs List */}
          {loading ? (
            <div className="text-center py-12">
              <div className="h-12 w-12 animate-spin rounded-full border-4 border-solid border-brand-teal border-r-transparent mx-auto"></div>
            </div>
          ) : jobs.length === 0 ? (
            <div className="bg-white p-12 rounded-2xl border border-slate-100 text-center">
              <Briefcase className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-2">
                {language === 'en' ? 'No Openings Currently' : 'वर्तमान में कोई पद खाली नहीं है'}
              </h3>
              <p className="text-muted-foreground">
                {language === 'en'
                  ? 'Please check back later for new opportunities.'
                  : 'कृपया नए अवसरों के लिए बाद में देखें।'}
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {jobs.map((job) => (
                <div
                  key={job.id}
                  className="bg-white p-6 sm:p-8 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow"
                  data-testid={`job-card-${job.id}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
                    <div>
                      <h2 className="text-2xl font-heading font-bold text-foreground mb-2">
                        {language === 'en' ? job.title_en : job.title_hi}
                      </h2>
                      <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                        <span className="flex items-center space-x-1">
                          <MapPin className="h-4 w-4" />
                          <span>{language === 'en' ? job.location_en : job.location_hi}</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <Clock className="h-4 w-4" />
                          <span>{jobTypeLabels[job.job_type]?.[language] || job.job_type}</span>
                        </span>
                        {job.salary_range && (
                          <span className="flex items-center space-x-1">
                            <DollarSign className="h-4 w-4" />
                            <span>{job.salary_range}</span>
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleApplyClick(job)}
                      className="bg-brand-teal text-white px-6 py-3 rounded-lg font-medium hover:bg-brand-teal-dark active:scale-95 transition-all duration-200 whitespace-nowrap"
                      data-testid={`apply-btn-${job.id}`}
                    >
                      {language === 'en' ? 'Apply Now' : 'अभी आवेदन करें'}
                    </button>
                  </div>

                  <p className="text-muted-foreground mb-4">
                    {language === 'en' ? job.description_en : job.description_hi}
                  </p>

                  {(job.requirements_en || job.requirements_hi) && (
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <h4 className="font-semibold text-foreground mb-2">
                        {language === 'en' ? 'Requirements:' : 'आवश्यकताएं:'}
                      </h4>
                      <p className="text-sm text-muted-foreground whitespace-pre-line">
                        {language === 'en' ? job.requirements_en : job.requirements_hi}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Application Form Modal */}
      {showApplicationForm && selectedJob && (
        <div className="fixed inset-0 bg-brand-navy/60 z-50 flex items-center justify-center p-4" onClick={() => setShowApplicationForm(false)}>
          <div 
            className="bg-white rounded-2xl p-6 sm:p-8 w-full max-w-md shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-2xl font-heading font-bold text-foreground mb-2">
              {language === 'en' ? 'Apply for Position' : 'पद के लिए आवेदन करें'}
            </h3>
            <p className="text-muted-foreground mb-6">
              {language === 'en' ? selectedJob.title_en : selectedJob.title_hi}
            </p>

            <form onSubmit={handleSubmitApplication} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  <User className="inline h-4 w-4 mr-1" />
                  {language === 'en' ? 'Full Name' : 'पूरा नाम'} *
                </label>
                <input
                  type="text"
                  required
                  value={applicationData.name}
                  onChange={(e) => setApplicationData({ ...applicationData, name: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-border bg-slate-50 focus:outline-none focus:ring-2 focus:ring-brand-teal"
                  placeholder={language === 'en' ? 'Enter your full name' : 'अपना पूरा नाम दर्ज करें'}
                  data-testid="applicant-name-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  <Phone className="inline h-4 w-4 mr-1" />
                  {language === 'en' ? 'Contact Number' : 'संपर्क नंबर'} *
                </label>
                <input
                  type="tel"
                  required
                  value={applicationData.phone}
                  onChange={(e) => setApplicationData({ ...applicationData, phone: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-border bg-slate-50 focus:outline-none focus:ring-2 focus:ring-brand-teal"
                  placeholder={language === 'en' ? 'Enter your phone number' : 'अपना फोन नंबर दर्ज करें'}
                  data-testid="applicant-phone-input"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-brand-teal text-white px-6 py-3 rounded-lg font-medium hover:bg-brand-teal-dark disabled:opacity-50 transition-all duration-200 flex items-center justify-center space-x-2"
                data-testid="submit-application-btn"
              >
                {submitting ? (
                  <span>{language === 'en' ? 'Submitting...' : 'जमा हो रहा है...'}</span>
                ) : (
                  <>
                    <span>{language === 'en' ? 'Submit Application' : 'आवेदन जमा करें'}</span>
                    {selectedJob?.google_form_url && <ExternalLink className="h-4 w-4 ml-1" />}
                  </>
                )}
              </button>
              {selectedJob?.google_form_url && (
                <p className="text-xs text-muted-foreground text-center mt-2">
                  {language === 'en' 
                    ? 'After submitting, you will be redirected to the detailed application form'
                    : 'जमा करने के बाद, आपको विस्तृत आवेदन फॉर्म पर भेजा जाएगा'}
                </p>
              )}
            </form>

            <button
              type="button"
              onClick={() => setShowApplicationForm(false)}
              className="w-full mt-4 text-muted-foreground hover:text-foreground text-sm"
            >
              {language === 'en' ? 'Cancel' : 'रद्द करें'}
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Jobs;
